export type PlanConsentFormattableAmount = {
  value: number
  currencyCode: string
}

export function isPlanConsentAcceptedLikeStatus(status: string): boolean {
  const key = (status?.trim() ?? "").toLowerCase()
  return key === "accepted" || key === "approved"
}

export function getPlanConsentAmountIntervalParts(
  formatAmount: (amount: PlanConsentFormattableAmount, precision?: number) => string,
  amount: PlanConsentFormattableAmount,
  intervalValue: number,
  intervalUnit: string
): { amountText: string; intervalText: string } {
  const n = intervalValue
  const unit = intervalUnit.trim().toLowerCase()
  const precision = amount.value % 100 === 0 ? 0 : 2
  const amountStr = formatAmount(amount, precision)

  if (!Number.isFinite(n) || n < 1) {
    return { amountText: amountStr, intervalText: `/${intervalUnit}` }
  }

  if (n === 1) {
    const single: Record<string, string> = {
      day: "day",
      week: "WK",
      month: "MO",
      quarter: "QTR",
      year: "YR",
    }
    const suffix = single[unit]
    if (suffix) {
      return { amountText: amountStr, intervalText: `/${suffix}` }
    }
  }

  if (unit === "week") {
    return { amountText: amountStr, intervalText: `/${n} wks` }
  }
  if (unit === "month") {
    return { amountText: amountStr, intervalText: `/${n} mo` }
  }
  if (unit === "year") {
    return { amountText: amountStr, intervalText: `/${n} yr` }
  }
  if (unit === "day") {
    return { amountText: amountStr, intervalText: `/${n} days` }
  }
  if (unit === "quarter") {
    return { amountText: amountStr, intervalText: `/${n} qtrs` }
  }

  return { amountText: amountStr, intervalText: `/${n} ${intervalUnit}` }
}

export function getPlanConsentRecurrencePhrase(
  intervalValue: number,
  intervalUnit: string
): string {
  const n = intervalValue
  const unit = intervalUnit.trim().toLowerCase()

  const unitSingular: Record<string, string> = {
    day: "day",
    week: "week",
    month: "month",
    year: "year",
    quarter: "quarter",
  }
  const unitPlural: Record<string, string> = {
    day: "days",
    week: "weeks",
    month: "months",
    year: "years",
    quarter: "quarters",
  }

  if (!Number.isFinite(n) || n < 1) {
    return "on the schedule in your agreement"
  }

  if (n === 1) {
    const u = unitSingular[unit]
    if (u) {
      return `once per ${u}`
    }
  }

  const pl = unitPlural[unit]
  if (pl) {
    return `every ${n} ${pl}`
  }

  return `every ${n} ${intervalUnit}`
}

export function formatPlanConsentFirstPaymentDate(isoDate: string): string {
  if (!isoDate) {
    return ""
  }
  const normalized = isoDate.includes("T") ? isoDate : `${isoDate}T12:00:00`
  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) {
    return ""
  }
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
}
